import React, { useEffect, useRef, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import RobustAssessmentPDF from "@/components/assessment/RobustAssessmentPDF";

const safeFilename = (value) => {
  const normalized = String(value || "adaptive-physiology-nervous-system-profile-v5")
    .trim()
    .replace(/\.pdf$/i, "")
    .replace(/[^a-z0-9-_]+/gi, "-")
    .replace(/^-+|-+$/g, "");
  return `${normalized || "adaptive-physiology-nervous-system-profile-v5"}.pdf`;
};

const RobustAssessmentPdfButton = ({
  answers,
  profile,
  date,
  filename,
  label = "Download PDF",
  busyLabel = "Preparing PDF",
  className = "inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm forr-btn-secondary disabled:opacity-50",
}) => {
  const [renderPdf, setRenderPdf] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const pdfRef = useRef(null);

  useEffect(() => {
    if (!renderPdf || !downloading) return undefined;
    let cancelled = false;

    const generate = async () => {
      try {
        await new Promise((resolve) =>
          requestAnimationFrame(() => requestAnimationFrame(resolve)),
        );
        if (document.fonts?.ready) {
          await document.fonts.ready;
        }
        if (cancelled || !pdfRef.current) return;

        const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
          import("html2canvas"),
          import("jspdf"),
        ]);
        const pages = Array.from(
          pdfRef.current.querySelectorAll("[data-robust-pdf-page]"),
        );
        if (!pages.length) {
          throw new Error("The nervous-system PDF pages did not render.");
        }

        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "pt",
          format: "a4",
          compress: true,
        });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        for (let index = 0; index < pages.length; index += 1) {
          const canvas = await html2canvas(pages[index], {
            scale: 2,
            useCORS: true,
            backgroundColor: "#FBF9F4",
            logging: false,
            windowWidth: 794,
          });
          if (cancelled) return;
          if (index > 0) pdf.addPage();
          pdf.addImage(
            canvas.toDataURL("image/jpeg", 0.95),
            "JPEG",
            0,
            0,
            pageWidth,
            pageHeight,
            undefined,
            "FAST",
          );
        }

        pdf.save(safeFilename(filename));
      } catch (error) {
        console.error("Nervous-system PDF generation failed", error);
      } finally {
        if (!cancelled) {
          setDownloading(false);
          setRenderPdf(false);
        }
      }
    };

    generate();
    return () => {
      cancelled = true;
    };
  }, [answers, date, downloading, filename, profile, renderPdf]);

  const download = () => {
    if (downloading) return;
    setDownloading(true);
    setRenderPdf(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={download}
        disabled={downloading}
        className={className}
      >
        {downloading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <Download size={16} />
        )}
        {downloading ? busyLabel : label}
      </button>
      {renderPdf && (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            left: "-20000px",
            top: 0,
            width: "794px",
            backgroundColor: "#FBF9F4",
          }}
        >
          <RobustAssessmentPDF
            ref={pdfRef}
            answers={answers}
            profile={profile}
            date={date}
          />
        </div>
      )}
    </>
  );
};

export default RobustAssessmentPdfButton;
